import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { updateFavoriteLocally, updateWishlist, selectPermissions } from '../../redux/permissionsSlice';
import { styled } from '@mui/material/styles';
import { Card, Typography, Grid, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from '@mui/material';
import ReportIcon from '@mui/icons-material/Report';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

const ReportCard = styled(Card)(({ theme }) => ({
    borderRadius: '6px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: '90%',
    height: '80px',
    border: `1px solid ${theme.palette.divider}`,
    margin: '4px',
    transition: 'none',
    cursor: 'pointer',
}));

const Header = styled(Typography)(({ theme }) => ({
    fontWeight: '700',
    fontSize: '16px',
    color: '#635bff',
    textAlign: 'center',
    letterSpacing: '1px',
    marginBottom: '0',
}));

const ReportBox = ({ title, link, UserPrevId }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const permissions = useSelector(selectPermissions);
    const permissionItem = permissions?.find(p => p.ModuleName === title);
    const finalUserPrevId = UserPrevId || (permissionItem ? permissionItem.UserPrevId : null);

    const [isWishlisted, setIsWishlisted] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        if (finalUserPrevId) {
            const currentWishlistStatus = permissionItem ? permissionItem.wishlist : false;
            setIsWishlisted(currentWishlistStatus);
        }
    }, [finalUserPrevId, permissionItem]);

    const handleCardClick = () => {
        if (link) {
            navigate(link);
        }
    };

    const handleWishlistClick = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isWishlisted) {
            setOpenDialog(true);
        } else {
            if (finalUserPrevId) {
                setIsWishlisted(true);
                dispatch(updateFavoriteLocally({ UserPrevId: finalUserPrevId, wishlist: true }));
                dispatch(updateWishlist({ UserPrevId: finalUserPrevId, wishlist: true }));
            }
        }
    };

    const handleConfirmUnpin = () => {
        if (finalUserPrevId) {
            setIsWishlisted(false);
            dispatch(updateFavoriteLocally({ UserPrevId: finalUserPrevId, wishlist: false }));
            dispatch(updateWishlist({ UserPrevId: finalUserPrevId, wishlist: false }));
        }
        setOpenDialog(false);
    };

    const handleCancelUnpin = () => {
        setOpenDialog(false);
    };

    return (
        <>
            <Grid item xs={12} sm={6} md={4} lg={3}>
                <ReportCard onClick={handleCardClick}>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 5,
                            left: 5,
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: 'transparent',
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <ReportIcon sx={{ color: '#635bff' }} />
                    </IconButton>

                    <Header>{title}</Header>

                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 5,
                            right: 5,
                            zIndex: 10,
                            '&:hover': {
                                backgroundColor: 'transparent',
                            }
                        }}
                        onClick={handleWishlistClick}
                    >
                        {isWishlisted ? (
                            <PushPinIcon
                                sx={{
                                    width: "30px",
                                    height: "30px",
                                    color: '#635bff',
                                    borderRadius: "50%",
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    transition: 'transform 0.3s',
                                }}
                            />
                        ) : (
                            <PushPinOutlinedIcon
                                sx={{
                                    width: "30px",
                                    height: "30px",
                                    color: '#635bff',
                                    opacity: 0.6,
                                    borderRadius: "50%",
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    transition: 'transform 0.3s',
                                }}
                            />
                        )}
                    </IconButton>
                </ReportCard>
            </Grid>

            <Dialog
                open={openDialog}
                onClose={handleCancelUnpin}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" color='warning'>
                    Confirm Unpin
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to unpin this report card "{title}"?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' size='small' onClick={handleCancelUnpin} color="error">
                        No
                    </Button>
                    <Button variant='contained' size='small' onClick={handleConfirmUnpin} color="success" autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ReportBox;